import { v4 as uuidv4 } from 'uuid';

import { getCookie, setCookie } from './cookies';

const OSP_BROWSER_ID_KEY = 'ajs_anonymous_id';
const OSP_SESSION_ID_KEY = '_osp_sid';
const WIDGET_BROWSER_ID_KEY = '_widget_id';
const WIDGET_SESSION_ID_KEY = '_widget_sid';
const ONE_YEAR_MS = 1000 * 60 * 60 * 24 * 365;

// Explicit multi-part public suffixes where a registrable domain needs three
// labels (e.g. "asurion.co.uk"). Stored as "secondLevel.tld" strings to avoid
// false positives: a check like secondLevel ∈ {co, com} AND tld.length === 2
// would wrongly match generics like "co.io" or "com.ai" that are NOT multi-part
// suffixes. When a 2-char TLD is seen but the pair isn't in this set,
// getRootDomain returns undefined so the cookie stays host-only rather than
// being set on a potential public suffix (e.g. "com.au"). Extend the set if a
// partner is onboarded under a ccTLD suffix not covered here.
const MULTI_PART_SUFFIXES = new Set([
  // United Kingdom
  'co.uk',
  'org.uk',
  'net.uk',
  'gov.uk',
  'ac.uk',
]);

const isIpv4 = (host: string): boolean => /^\d{1,3}(\.\d{1,3}){3}$/.test(host);

/**
 * Resolve the registrable ("root") domain of a hostname so a cookie can be
 * shared across its subdomains, e.g. `widget.asurion.com` -> `asurion.com` and
 * `widget.asurion.co.uk` -> `asurion.co.uk`. Returns `undefined` when there is
 * no registrable domain to scope to (single-label hosts like `localhost`, IPv4
 * addresses, or empty input), in which case the caller omits the Domain
 * attribute and the cookie stays host-only.
 */
export const getRootDomain = (hostname: string): string | undefined => {
  const host = hostname.toLowerCase().replace(/\.$/, '');
  if (!host || isIpv4(host)) {
    return undefined;
  }
  const labels = host.split('.');
  if (labels.length < 2) {
    return undefined;
  }
  const tld = labels[labels.length - 1];
  const secondLevel = labels[labels.length - 2];
  const isMultiPartSuffix = labels.length >= 3 && MULTI_PART_SUFFIXES.has(`${secondLevel}.${tld}`);
  // For 2-char TLDs (ccTLDs) where the suffix pair isn't in our list we cannot
  // safely determine the registrable domain — the fallback labels.slice(-2) would
  // return the public suffix itself (e.g. "com.au"), not the registrable domain.
  // Return undefined so the caller omits Domain= and the cookie stays host-only.
  if (tld.length === 2 && labels.length >= 3 && !isMultiPartSuffix) {
    return undefined;
  }
  return labels.slice(isMultiPartSuffix ? -3 : -2).join('.');
};

export class UserBrowser {
  readonly Id: string;
  readonly SessionId: string;

  constructor() {
    this.Id = this.getOrCreateId();
    this.SessionId = this.getOrCreateSessionId();
  }

  private getOrCreateId(): string {
    const ospId = getCookie(OSP_BROWSER_ID_KEY);
    if (ospId) return ospId;

    let id = getCookie(WIDGET_BROWSER_ID_KEY);
    if (!id) {
      id = uuidv4();
      setCookie(WIDGET_BROWSER_ID_KEY, id, {
        expires: new Date(Date.now() + ONE_YEAR_MS),
        domain: typeof window === 'undefined' ? undefined : getRootDomain(window.location.hostname),
        // First-party context only: defaults to SameSite=Lax. If the loader ever runs in a
        // cross-origin iframe, restore SameSite=none so the cookie remains readable there.
      });
    }
    return id;
  }

  private getOrCreateSessionId(): string {
    const ospSessionId = getCookie(OSP_SESSION_ID_KEY);
    if (ospSessionId) return ospSessionId;

    let sessionId = getCookie(WIDGET_SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = uuidv4();
      setCookie(WIDGET_SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  }
}
